import { FC, memo, useCallback, useState } from 'react';
import { Form, Input, Modal, Select } from 'antd';
import { useUIDispatchContext, useModalStateContext } from 'providers/UI';
import { useTranslation } from 'react-i18next';
import { createAdminPaymentAppointment } from 'services/api/appointments';

import { PATTERNS } from 'utils/validation';
import { showErrorMessage } from 'utils/showErrorMessage';
import {
  PATIENT_TYPE,
  PATIENT_TYPE_VALUES,
  PatientPaymentType
} from '../../../../types/api/appointments';

const { TextArea } = Input;

interface IProps {
  onSaved?: () => void;
}

const SessionsPay: FC<IProps> = ({ onSaved }) => {
  const { t } = useTranslation();
  const { sessionPay } = useModalStateContext();
  const { toggleModal } = useUIDispatchContext();
  const [form] = Form.useForm();
  const [isCreating, setIsCreating] = useState(false);

  const closeModal = useCallback(() => {
    form.resetFields();
    toggleModal('sessionPay', false);
  }, [form, toggleModal]);

  const handleSubmit = useCallback(
    async (values: {
      price: number;
      patientPaymentType: PatientPaymentType;
      reason?: string;
    }) => {
      try {
        setIsCreating(true);
        await createAdminPaymentAppointment({
          price: Number(values.price),
          patientPaymentType: values.patientPaymentType,
          reason: values.reason
        });
        onSaved?.();
        closeModal();
      } catch (err) {
        showErrorMessage(err);
      } finally {
        setIsCreating(false);
      }
    },
    [closeModal, onSaved]
  );

  return (
    <Modal
      open={sessionPay.visible}
      onCancel={closeModal}
      onOk={form.submit}
      confirmLoading={isCreating}
      title={t('sessions.create')}
    >
      <Form form={form} layout="vertical" size="large" onFinish={handleSubmit}>
        <Form.Item
          name="price"
          label={t('sessions.price')}
          rules={[{ required: true }, { pattern: PATTERNS.price }]}
        >
          <Input maxLength={255} />
        </Form.Item>
        <Form.Item
          name="patientPaymentType"
          label={t('sessions.patientPaymentType')}
          rules={[{ required: true }]}
        >
          <Select
            allowClear
            style={{ width: 220 }}
            size="large"
            value={PATIENT_TYPE.platform_client_online}
            options={PATIENT_TYPE_VALUES.map(item => ({
              label: item,
              value: item
            }))}
          />
        </Form.Item>
        <Form.Item name="reason" label={t('sessions.notes')}>
          <TextArea />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default memo(SessionsPay);
